/** Types */
export type LatLngLiteral = google.maps.LatLngLiteral;
type GetMapCoordinatesFromAddress = (address?: string) => Promise<LatLngLiteral | undefined>;
type GetAddressFromCoordinates = (position?: LatLngLiteral) => Promise<string | undefined>;
type GetAddressComponentsFromCoordinates = (position?: LatLngLiteral) => Promise<google.maps.GeocoderAddressComponent[] | undefined>;

/**
 * @description Get Google map coordinates from address
 * - `address` - The address as string.
 */
export const getMapCoordinatesFromAddress: GetMapCoordinatesFromAddress = async (address) => {
  // Return default position if no address
  const position = { lat: 45.9432, lng: 24.9668 } as LatLngLiteral;
  if (!address) return position;

  // Get coordinates from address
  const geoCoder = new google.maps.Geocoder();
  await geoCoder?.geocode({ address }, (results, status) => {
    if (status === google.maps.GeocoderStatus.OK) {
      const newLat = results?.[0].geometry.location.lat();
      const newLng = results?.[0].geometry.location.lng();
      if (newLat && newLng) {
        position.lat = newLat;
        position.lng = newLng;
      }
    }
  });

  return position;
};

/**
 * @description Get address from Google map coordinates
 * - `position` - The position as LatLngLiteral.
 */
export const getAddressFromCoordinates: GetAddressFromCoordinates = async (position) => {
  // Return undefined if no position
  let address: string | undefined;
  if (!position) return address;

  const geocoder = new google.maps.Geocoder();
  await geocoder.geocode({ location: position }, (results, status) => {
    if (status === google.maps.GeocoderStatus.OK) {
      address = results?.[0]?.formatted_address;
    }
  });

  return address;
};

/**
 * @description Get address components from Google map coordinates
 * - `position` - The position as LatLngLiteral.
 */
export const getAddressComponentsFromCoordinates: GetAddressComponentsFromCoordinates = async (position) => {
  // Return undefined if no position
  let address: google.maps.GeocoderAddressComponent[] | undefined;
  if (!position) return address;

  const geocoder = new google.maps.Geocoder();
  await geocoder.geocode({ location: position }, (results, status) => {
    if (status === google.maps.GeocoderStatus.OK) {
      address = results?.[0]?.address_components;
    }
  });

  return address;
};
